<script setup>
import { computed, ref } from "vue";
import { useCouponStore } from "@/stores/useCouponStore";
import DeleteConfirmModal from "@/components/Generics/DeleteConfirmModal.vue";

// ✅ Emite notify, igual ao padrão dos formulários de exclusão em lote.
const emit = defineEmits(["close", "refresh", "notify"]);
const store = useCouponStore();

// ✅ IDs selecionados APENAS da página atual.
// Mantém o mesmo comportamento do BatchDeleteActivityForm:
// se houver seleção acumulada em outras páginas, este modal considera só a página visível.
const selectedIdsOnCurrentPage = computed(() => {
  const idsOnPage = store.coupons.map((c) => c.couponId);
  return store.selectedCouponIds.filter((id) => idsOnPage.includes(id));
});

// ✅ Quantidade selecionada NA PÁGINA.
const selectedCount = computed(() => selectedIdsOnCurrentPage.value.length);

// ✅ Congela ao abrir para não trocar o texto quando a store limpar seleção.
const frozenCount = ref(selectedCount.value);
if (!frozenCount.value || frozenCount.value < 1) frozenCount.value = 1;

// ✅ Mensagem de confirmação com singular/plural.
const messageTitle = computed(() => {
  if (frozenCount.value === 1)
    return "Tem certeza que deseja apagar o coupon selecionado?";
  return `Tem certeza que deseja apagar os ${frozenCount.value} coupons selecionados?`;
});

function buildBatchDeleteCouponError(error) {
  const raw =
    error?.response?.data?.message ||
    error?.message ||
    "Ocorreu um erro ao excluir os coupons.";
  return raw;
}

async function onConfirmBatchDelete() {
  try {
    await store.deleteSelectedCoupons();

    // ✅ Toast genérico, sem código dos coupons.
    emit("notify", {
      type: "success",
      message:
        frozenCount.value === 1
          ? "Coupon apagado com sucesso."
          : "Coupons apagados com sucesso.",
    });

    emit("refresh");
    emit("close");
  } catch (error) {
    const msg = buildBatchDeleteCouponError(error);

    emit("notify", {
      type: "error",
      message: msg,
    });

    // Deixa o DeleteConfirmModal saber que deu erro para parar loading.
    throw new Error(msg);
  }
}
</script>

<template>
  <DeleteConfirmModal
    :show="true"
    title="Apagar Coupons em Lote"
    :messageTitle="messageTitle"
    messageSubtitle="Os coupons selecionados serão apagados. Esta ação não pode ser desfeita."
    confirmText="Sim, apagar"
    cancelText="Cancelar"
    loadingText="Apagando..."
    :onConfirm="onConfirmBatchDelete"
    @close="emit('close')"
  />
</template>
